import { LitElement, css, html } from 'lit';
import { customElement, query, state } from 'lit/decorators';
import { intoPointRequests, isDefined, undefinedIfEmpty } from '../helpers/utils';

import { GetPointResponse } from '../types';
import { TextField } from '@material/mwc-textfield';
import { getMainPage } from '../helpers/page-name';
import pointCache from '../helpers/state/point-cache';
import topicData from '../helpers/state/topic-data';
import { updatePoints } from '../helpers/api/points-api';

@customElement('gated-access-select-points-modal')
export class GatedAccessSelectPointsModal extends LitElement {

    @query('#searchText')
    private readonly searchTextElement!: TextField;

    @state()
    searchText: string = '';

    @state()
    selectedPoints: GetPointResponse[] = [];

    private async toggleSelection(point: GetPointResponse) {
        const foundIndex = this.selectedPoints?.findIndex((p) => p._id?.$oid === point._id?.$oid);
        if (foundIndex > -1) {
            this.selectedPoints.splice(foundIndex, 1);
        } else {
            this.selectedPoints.push(point);
        }
        await this.requestUpdate();
    }

    private handleClickSearch() {
        this.searchText = undefinedIfEmpty(this.searchTextElement.value?.trim().toLocaleLowerCase()) ?? '';
    }

    private async clearAll() {
        this.selectedPoints = [];
        await this.requestUpdate();
    }

    private async handleClickSave() {
        const points = pointCache.allPoints.map((point) => ({
            ...point,
            is_gate_hidden: !isDefined(this.selectedPoints.find(item => item._id.$oid === point._id.$oid))
        }));

        await getMainPage().errorToastElement?.tryOrErrorToast(async () => {
            await updatePoints('Gated Access Select Points', topicData.topicId, intoPointRequests(points), topicData.topicKey);

            // Refresh point cache BEFORE closing modal
            if (topicData.topic !== undefined) {
                await pointCache.instantiatePointCache(topicData.topic, topicData.topicKey, true);
            }

            this.dispatchEvent(new CustomEvent('close'));
        }, 'Error updating points.');
    }

    render() {

        return html`
            <div class="modalContent">
                <div class="modal__content">
                    <h3 class="modal__header">Select points available for preview</h3>

                    <div class="search-container">
                        <mwc-textfield
                            id="searchText"
                            class="input__search"
                            helper="Search for a point"
                            icon="search"
                            outlined>
                        </mwc-textfield>
                        <mwc-button
                            class="search-button"
                            raised
                            @click="${this.handleClickSearch}"
                        >
                            Search
                        </mwc-button>
                    </div>

                    <div class="search-result">
                        <div class="result">
                            <b>${this.selectedPoints.length}</b> points selected
                        </div>
                        <a class="clear-all"  @click="${this.clearAll}">Clear All</a>
                    </div>

                    <div class="points-list">
                        ${pointCache.allPoints
                            .filter((point) => JSON.stringify(point).toLowerCase().includes(this.searchText))
                            .map((point: GetPointResponse) => html`
                                <div class="list-item">
                                    <div class="point-info">
                                        <span class="category-name">
                                            ${topicData?.topic?.categories.find(
                                                (c) => c._id?.$oid === point.topic_category_id.$oid
                                            )?.name}
                                        </span>
                                        <span class="place-name">${point.name ?? point.location.search}</span>
                                    </div>
                                    <div class="select-status">
                                        <form-checkbox
                                            .checked=${isDefined(this.selectedPoints.find(item => item._id.$oid === point._id.$oid))}
                                            @change=${async () => await this.toggleSelection(point)}
                                        ></form-checkbox>
                                    </div>
                                </div>
                            `)}
                    </div>
                </div>

                <div class="modal__footer">
                    <mwc-button raised @click="${this.handleClickSave}">
                        Save
                    </mwc-button>
                </div>
            </div>
        `;
    }

    static get styles() {
        return css`
            .modalContent {
                position: relative;
                width: 100%;
                display: flex;
                max-height: 70vh;
                min-height: 30vh;
                flex-direction: column;
            }

            h3.modal__header {
                color: var(--main-primary-color);
                text-align: center;
                font-size: 20px;
                font-weight: 700;
                line-height: 130%;
                margin: 1.5rem 0;
            }

            .modal__content {
                width: 800px;
                max-width: 100%;
                padding: 0 1rem;
                flex: 1;
                overflow: hidden auto;
                box-sizing: border-box;
            }

            .modal__footer {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: flex-end;
                padding-top: 1rem;
                box-sizing: border-box;
            }

            .search-container {
                display: flex;
                padding: 2rem 1.5rem 0.5rem 1.5rem;
                justify-content: space-between;
                gap: 1rem;
                border-radius: 0.5rem;
                border: 1px solid var(--theme-border-color);
                background: white;
                box-shadow: 0px 4px 12px 0px var(--theme-border-color);
                margin-bottom: 1.5rem;
            }

            .input__search {
                flex: 1;
            }

            .search-button {
                --mdc-theme-primary: var(--theme-lighter-text-color);
                --mdc-button-outline-color: var(--theme-lighter-text-color);
                --mdc-typography-button-text-transform: case;
                margin-top: 10px;
            }

            .points-list {
                width: 100%;
                flex: 1;
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                justify-content: flex-start;
                overflow-y: auto;
                gap: 0.5rem;
            }

            .list-item {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 1rem;
            }

            .point-info {
                flex: 1;
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                justify-content: flex-start;
            }

            .category-name {
                color: var(--theme-input-color);
                font-family: Roboto;
                font-size: 12px;
                font-weight: 500;
                line-height: 16px;
                letter-spacing: 0.5px;
            }

            .place-name {
                color: var(--main-primary-color);
                font-family: Roboto;
                font-size: 16px;
                line-height: 24px;
                letter-spacing: 0.5px;
            }

            .select-status {
                width: 70px;
                display: flex;
                align-items: center;
                justify-content: center;
            }

            .search-result {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 1rem;
                margin-bottom: 0.5rem;
            }

            .result {
                flex: 1;
                color: var(--main-primary-color);
                font-size: 16px;
                line-height: 150%;
            }

            .result > b {
                color: var(--main-primary-color);
                font-size: 16px;
                font-weight: 700;
                line-height: 150%;
            }

            .clear-all {
                width: 70px;
                text-align: center;
                cursor: pointer;
            }
        `;
    }
}